/**
 * useInstituteFeatureFlag
 *
 * Retrieves the backend-driven feature-flag map for the current institute
 * and returns whether a specific flag is enabled.
 *
 * Design: opt-out / fail-open model.
 *   - While loading  → returns true  (don't block the UI)
 *   - On error       → returns true  (throwOnError: false; don't break the page)
 *   - Key absent     → returns true  (unknown flag = allow by default)
 *   - Flag = false   → returns false (explicitly disabled)
 *
 * This hook runs in parallel with the subscription-plan hook (useFeatureFlag.ts)
 * which should NOT be modified.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authstore';
import { getInstituteFeatureFlags } from '@/services/api/featureFlags';

/** Stable query key — exported so callers can invalidate the cache. */
export const INSTITUTE_FEATURE_FLAGS_QUERY_KEY = ['institute-feature-flags'] as const;

/**
 * Returns whether the given feature flag is enabled for the current institute.
 *
 * @param featureKey - The flag name as defined in the backend FeatureKeys constants.
 * @returns `true` while loading / on error / when the key is absent; the actual
 *          boolean value once the data is available.
 */
export function useInstituteFeatureFlag(featureKey: string): boolean {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const { data } = useQuery({
    queryKey: INSTITUTE_FEATURE_FLAGS_QUERY_KEY,
    queryFn: getInstituteFeatureFlags,
    staleTime: 300_000, // 5 minutes
    enabled: isAuthenticated,
    throwOnError: false,
  });

  // Fail-open: undefined data (loading / error) or absent key → true
  if (data === undefined) return true;
  return data[featureKey] ?? true;
}
